import React, { createContext, useContext, useEffect, useState } from 'react';

interface WishlistContextValue {
  ids: number[];
  isWishlisted: (id: number) => boolean;
  toggleWishlist: (id: number) => void;
}

const STORAGE_KEY = 'bfh_wishlist_v1';
const WishlistContext = createContext<WishlistContextValue | null>(null);

const readStored = (): number[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((n): n is number => typeof n === 'number') : [];
  } catch {
    return [];
  }
};

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ids, setIds] = useState<number[]>(readStored);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ids)); } catch { /* storage unavailable — feature degrades to session-only */ }
  }, [ids]);

  const isWishlisted = (id: number): boolean => ids.includes(id);

  const toggleWishlist = (id: number): void => {
    setIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <WishlistContext.Provider value={{ ids, isWishlisted, toggleWishlist }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = (): WishlistContextValue => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within a WishlistProvider');
  return ctx;
};
